import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getMyEmails } from '../api/users'
import type { UserEmailOut } from '../api/users'
import { apiFetch } from '../api/client'

interface DocSummary {
  documentType: string
  documentNumber: string
}

interface Props {
  endpoint: string
  requestBody: Record<string, unknown>
  /** Pre-submit resync (REQ-TESTDATA-001) voor de dynamische nummer-velden
   * van deze set (bv. `document_number`/`credit_document_number`) — de
   * payload MOET de hieruit geretourneerde waarden gebruiken. */
  resolveDynamicFields: () => Promise<Record<string, unknown>>
  documents: DocSummary[]
  onClose: () => void
  onSent?: () => void
}

const DOC_TYPE_KEYS: Record<string, string> = {
  invoice: 'testdata_email_modal.doc_invoice',
  order_response: 'testdata_email_modal.doc_order_response',
  despatch_advice: 'testdata_email_modal.doc_despatch_advice',
  credit_note: 'testdata_email_modal.doc_credit_note',
}

const PREFS_KEY = 'testdata_set_email_prefs'

// Zie TestdataEmailModal.tsx — zelfde formaten ondersteunen PDF-embedding in de XML.
const EMBEDDABLE_XML_FORMATS = new Set(['sales005', 'ubl21'])

function loadPrefs(): { email: string; includePdf: boolean; includeXml: boolean; embedPdfInXml: boolean } | null {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function savePrefs(email: string, includePdf: boolean, includeXml: boolean, embedPdfInXml: boolean) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify({ email, includePdf, includeXml, embedPdfInXml })) } catch { /* quota */ }
}

/** E-mailmodal voor een set van meerdere testdocumenten (debet/credit-set, foutinjectieset)
 * in één bericht — parametriseerbaar naar endpoint en documentenlijst, i.t.t. de
 * single-document TestdataEmailModal. */
export function TestdataSetEmailModal({ endpoint, requestBody, resolveDynamicFields, documents, onClose, onSent }: Props) {
  const { t } = useTranslation()
  const prefs = loadPrefs()
  const [emailAddresses, setEmailAddresses] = useState<UserEmailOut[]>([])
  const [selectedEmail, setSelectedEmail] = useState(prefs?.email ?? '')
  const [includePdf, setIncludePdf] = useState(prefs?.includePdf ?? true)
  const [includeXml, setIncludeXml] = useState(prefs?.includeXml ?? true)
  const [embedPdfInXml, setEmbedPdfInXml] = useState(prefs?.embedPdfInXml ?? true)
  // Bewust niet in prefs/localStorage bewaard — eenmalige toevoeging per verzending,
  // geen blijvende voorkeur. Wordt aan beide mails van de set toegevoegd.
  const [subjectNote, setSubjectNote] = useState('')
  const [phase, setPhase] = useState<'confirm' | 'sending' | 'done' | 'error'>('confirm')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState('')
  // Vastgezet op het moment van versturen — `documents` is live en springt door
  // onSent (refreshDocCounter) meteen door naar de VOLGENDE nummers.
  const [sentDocuments, setSentDocuments] = useState<DocSummary[] | null>(null)
  const displayDocuments = sentDocuments ?? documents

  useEffect(() => {
    getMyEmails().then(list => {
      setEmailAddresses(list)
      const saved = prefs?.email
      const match = saved ? list.find(e => e.email === saved) : null
      if (match) {
        setSelectedEmail(match.email)
      } else if (!selectedEmail) {
        const primary = list.find(e => e.is_primary) ?? list[0]
        if (primary) setSelectedEmail(primary.email)
      }
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const attachmentLabel = [includePdf && 'PDF', includeXml && 'XML'].filter(Boolean).join(' + ') || '—'
  const canSend = selectedEmail.trim() !== '' && (includePdf || includeXml)
  const showEmbedOption = includeXml && EMBEDDABLE_XML_FORMATS.has(requestBody.xml_format as string)

  async function handleSend() {
    // Re-entrancy-guard: voorkomt een dubbele verzending bij een dubbele klik
    // terwijl de eerste aanvraag nog loopt.
    if (!canSend || phase !== 'confirm') return
    setPhase('sending')
    setErrorMsg(null)
    try {
      // Verse, mogelijk-hergesyncte nummers ophalen vlak vóór submit. Dit is
      // slechts advisory/UX (vult de payload en houdt de UI-suggestie
      // actueel) — voor auto_derived-nummers is de backend authoritative en
      // kan het daadwerkelijk gebruikte nummer afwijken van deze gok, met
      // name onder concurrency (REQ-TESTDATA-002). De response hieronder
      // draagt daarom de échte, authoritative nummers.
      const dynamic = await resolveDynamicFields()
      const sendResult = await apiFetch<{ documents?: Record<string, string> }>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          ...requestBody, ...dynamic, to_address: selectedEmail, include_pdf: includePdf, include_xml: includeXml,
          embed_pdf_in_xml: showEmbedOption ? embedPdfInXml : true,
          subject_note: subjectNote.trim() || null,
        }),
      })
      savePrefs(selectedEmail, includePdf, includeXml, embedPdfInXml)
      setSentTo(selectedEmail)
      setSentDocuments(documents.map(d => ({
        ...d,
        documentNumber:
          sendResult.documents?.[d.documentType] ??
          (d.documentType === 'invoice' && typeof dynamic.document_number === 'string' ? dynamic.document_number :
           d.documentType === 'credit_note' && typeof dynamic.credit_document_number === 'string' ? dynamic.credit_document_number :
           d.documentNumber),
      })))
      setPhase('done')
      onSent?.()
    } catch (e: unknown) {
      setErrorMsg(e instanceof Error ? e.message : t('testdata_email_modal.error_send'))
      setPhase('error')
    }
  }

  const docLabel = (dt: string) => (DOC_TYPE_KEYS[dt] ? t(DOC_TYPE_KEYS[dt]) : dt)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md p-6 space-y-5">

        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {t('testdata_set_email_modal.title')}
            </h2>
            <ul className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 space-y-0.5">
              {displayDocuments.map(d => (
                <li key={d.documentNumber}>{docLabel(d.documentType)}: {d.documentNumber}</li>
              ))}
            </ul>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>

        {phase === 'confirm' && (
          <>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includePdf}
                  onChange={e => setIncludePdf(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">PDF</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeXml}
                  onChange={e => setIncludeXml(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">XML</span>
              </label>
              {!includePdf && !includeXml && (
                <span className="text-xs text-red-500 dark:text-red-400">{t('testdata_email_modal.select_attachment')}</span>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {attachmentLabel} — {t('testdata_set_email_modal.separate_emails_note', { count: documents.length })}
            </p>

            {showEmbedOption && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={embedPdfInXml}
                  onChange={e => setEmbedPdfInXml(e.target.checked)}
                  className="w-4 h-4 rounded accent-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{t('testdata_email_modal.embed_pdf_label')}</span>
              </label>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('testdata_email_modal.subject_note_label')}
              </label>
              <input
                type="text"
                value={subjectNote}
                onChange={e => setSubjectNote(e.target.value)}
                maxLength={150}
                placeholder={t('testdata_email_modal.subject_note_placeholder')}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('testdata_email_modal.send_to_label')}
              </label>
              {emailAddresses.length > 0 ? (
                <select
                  value={selectedEmail}
                  onChange={e => setSelectedEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {emailAddresses.map(addr => (
                    <option key={addr.id} value={addr.email}>
                      {addr.email}{addr.label ? ` (${addr.label})` : ''}{addr.is_primary ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('testdata_email_modal.no_emails_configured')}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-1">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSend}
                disabled={!canSend}
                className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 dark:disabled:bg-blue-800 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {t('testdata_email_modal.btn_send')}
              </button>
            </div>
          </>
        )}

        {phase === 'sending' && (
          <div className="flex items-center gap-3 py-4 text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-5 h-5 text-blue-500 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            {t('testdata_email_modal.sending_to', { email: selectedEmail })}
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4">
              <span className="shrink-0 w-6 h-6 rounded-full bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300 flex items-center justify-center">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">{t('testdata_email_modal.done_title')}</p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">{sentTo}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-5 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 text-sm text-red-700 dark:text-red-300">
              {errorMsg ?? t('testdata_email_modal.error_unknown')}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 transition-colors"
              >
                {t('common.close')}
              </button>
              <button
                onClick={() => setPhase('confirm')}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {t('testdata_email_modal.btn_retry')}
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
